import {get} from "lodash";
import {ioJsonAction, ioJsonFetchAction} from 'Core/store/actions/io';
import {hideLoading, hideLoadingFunction, showLoading} from 'Core/helpers/loading';
import {getArray} from "Core/helpers/data";
import {actionCreators} from "Core/store/reducers";
import * as listItemDataMap from "./dataMap/listItem";
import * as itemFilterDataMap from "./dataMap/filter";
import {reducerStoreKey} from "./reducer";
import {SORT_ORDER} from "Core/const/global";
import {isSuccessful} from "Core/helpers/io";
import {reducerStoreKey as reducerStoreKeyQuestionnaire} from "../../../pages/apps/default/studies/popups/QuestionnairePopup/reducer";
import {resultInput as resultInputQuestionnaire} from '../../../pages/apps/default/studies/popups/QuestionnairePopup/dataMap';

/**
 * Fetch user-submitted questionnaires list
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string} accountId - DB ID of the account (from URL).
 * @param {string} studyId - DB ID of the study (from URL).
 * @param {Object} [filter] - Fetch filter.
 * @param {number} [pageNo] - Ordinal of the page to load (pagination). Starts from 1.
 * @param {number} [perPage] - Number of items per page to load (pagination). Used system default if not specified.
 * @param {string} [sortBy='ordinal'] - Sort field name. The API defines sort fields.
 * @param {SortOrder} [sortDir='asc'] - Sort direction.
 * @return {function(*=): Promise<IoJsonFetchResponseObject>}
 */
export const fetchUserSubmittedQuestionnairesListAction = (
	abortCallback, accountId, studyId, filter = null, pageNo = 1, perPage, sortBy, sortDir = SORT_ORDER.ASC
) => dispatch => {
	return ioJsonFetchAction(
		abortCallback,
		'defaultAuthorizedApi',
		'member/questionnaire/fetch-external-questionnaires-by-account-id',
		'',
		filter,
		null,
		pageNo,
		perPage,
		sortBy,
		sortDir,
		{accountId, studyId},
	)(dispatch)
		// Get mapped data from response data
		.then(responseData => {
			if (isSuccessful(responseData)) {
				return ({
					...responseData,
					filter: itemFilterDataMap.input(get(responseData, 'filter')),
					data: getArray(responseData, 'data').map(i => listItemDataMap.input(i))
				});
			}
			return undefined;
		});
};

/**
 * Load user-submitted questionnaires list into Redux store
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string} accountId - DB ID of the account (from URL).
 * @param {string} studyId - DB ID of the study (from URL).
 * @param {Object} [filter] - Fetch filter.
 * @param {number} [pageNo] - Ordinal of the page to load (pagination). Starts from 1.
 * @param {number} [perPage] - Number of items per page to load (pagination). Used system default if not specified.
 * @param {string} [sortBy] - Sort field name. The API defines sort fields.
 * @param {SortOrder} [sortDir] - Sort direction.
 * @param {boolean} [loadingOverlay=true] - Flag that specifies if a loading overlay is rendered.
 * @return {function(*=): Promise<IoJsonFetchResponseObject>}
 */
export const loadUserSubmittedQuestionnairesListAction = (
	abortCallback, accountId, studyId, filter = null, pageNo = 1, perPage, sortBy, sortDir, loadingOverlay = true
) => dispatch => {
	const loading = (loadingOverlay === true ? showLoading("#user-submitted-questionnaires-table") : null);
	return fetchUserSubmittedQuestionnairesListAction(
		abortCallback, accountId, studyId, filter, pageNo, perPage, sortBy, sortDir
	)(dispatch)
		// Load data into Redux store
		.then(responseData => {
			if (responseData) dispatch(actionCreators[reducerStoreKey].setUserSubmittedQuestionnairesListData(responseData));
			if (loading !== null) hideLoading(loading);
			return responseData;
		});
};

/**
 * Clear user-submitted questionnaires list form Redux store
 *
 * @return {(function(*): void)|*}
 */
export const clearUserSubmittedQuestionnairesListAction = () => dispatch => {
	dispatch(actionCreators[reducerStoreKey].clearUserSubmittedQuestionnairesListData());
}

/**
 * Attach a user-submitted questionnaire to the study creating a new questionnaire.
 * 
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string} studyId
 * @param {string} id
 * @return {function(*): Promise<IoJsonResponseObject|undefined>}
 */
export const attachUserSubmittedQuestionnaireAction = (abortCallback, studyId, id) => dispatch => {
	const loading = showLoading('#item-attach-dialog .dialog-content-component > .buttons');
	return ioJsonAction(
		abortCallback,
		'defaultAuthorizedApi',
		'member/questionnaire/attach-external-questionnaire',
		{studyId, externalQuestionnaireId: id},
		hideLoadingFunction(loading),
	)(dispatch)
		// Load data into Redux store
		.then(response => {
			if (response) dispatch(actionCreators[reducerStoreKeyQuestionnaire].setResult(resultInputQuestionnaire(response.data)));
			else dispatch(actionCreators[reducerStoreKeyQuestionnaire].setResult(undefined));
			return response;
		});	
};